import {
  faClipboard,
  faClipboardCheck,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  badgeVariants,
  cn,
  EmailInput,
  MultiSelect,
  SimpleSelect,
  SwitchInput,
  TextAreaInput,
  TextInput,
} from 'corso-admin-ui';
import { Pegasus } from 'corso-types';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useCopyToClipboard } from 'usehooks-ts';
import { useAutomationFormContext } from '~/features/settings/automations/hooks/useAutomationForm';
import { useIntegrationSettingsData } from '~/hooks/useIntegrationSettings';

// sentinel for the clearable team select — Radix rejects an empty option value
const UNASSIGNED = 'unassigned';

function TemplateVarChip({ value }: { value: string }) {
  const [, copyToClipboard] = useCopyToClipboard();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      className={cn(badgeVariants({ hoverable: true }), 'font-mono')}
      onClick={() => {
        copyToClipboard(value)
          .then(() => setCopied(true))
          .catch((error: unknown) => console.error(error));
      }}
    >
      {value}
      <FontAwesomeIcon icon={copied ? faClipboardCheck : faClipboard} />
    </button>
  );
}

export default function EventCreateGorgiasTicket() {
  const { control } = useAutomationFormContext();

  const templateValues = Object.values(Pegasus.GorgiasTicketTemplateVars);

  const { data: { gorgiasConfig } = {} } = useIntegrationSettingsData();

  const teams =
    gorgiasConfig?.connected === true ? (gorgiasConfig.teams ?? []) : [];

  return (
    <>
      <Alert variant="tip" heading="Template Variables">
        <div>
          The following values can be used in Subject, Body, or Tags. Click to
          copy.
          <div className="mt-2 flex flex-wrap gap-2">
            {templateValues.map((v) => (
              <TemplateVarChip key={v} value={`{{${v}}}`} />
            ))}
          </div>
        </div>
      </Alert>
      <Controller
        control={control}
        name="event.params.senderEmail"
        render={({ field, fieldState }) => (
          <EmailInput
            id="gorgias-ticket-sender-email"
            label="Gorgias User"
            details="The email of the Gorgias user used to create the internal note on the customer's ticket."
            required
            {...field}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.shouldAssignSender"
        render={({ field: f }) => (
          <SwitchInput
            id="should-assign-sender"
            label="Assign Sender"
            details="When enabled, the Gorgias ticket will be assigned to the sender email."
            checked={f.value}
            onChange={f.onChange}
          />
        )}
      />
      <Controller
        control={control}
        name="event.params.assigneeTeamId"
        render={({ field: f, fieldState }) => (
          <SimpleSelect
            label="Assignee Team"
            details={
              teams.length > 0
                ? 'The Gorgias team the ticket will be assigned to.'
                : 'No teams available. Reconnect Gorgias to grant access to teams.'
            }
            options={[
              { value: UNASSIGNED, label: 'Not Assigned' },
              ...teams.map(({ id, name }) => ({
                value: `${id}`,
                label: name,
              })),
            ]}
            onChange={(next) =>
              f.onChange(
                next === UNASSIGNED ? undefined : Number.parseInt(next, 10),
              )
            }
            value={f.value ? `${f.value}` : UNASSIGNED}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="event.params.subject"
        render={({ field, fieldState }) => (
          <TextInput
            id="gorgias-ticket-subject"
            label="Subject"
            details="The subject line for the Gorgias ticket."
            required
            {...field}
            error={fieldState.error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="event.params.body"
        render={({ field, fieldState }) => (
          <TextAreaInput
            id="gorgias-ticket-body"
            label="Body"
            details="The body content for the Gorgias ticket."
            {...field}
            value={field.value ?? ''}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="event.params.tags"
        render={({ field, fieldState }) => (
          <MultiSelect
            label="Tags"
            details="Optional tags to apply to the Gorgias ticket."
            placeholder="Add tags"
            options={(field.value ?? []).map((tag) => ({
              label: tag,
              value: tag,
            }))}
            value={(field.value ?? []).map((tag) => ({
              label: tag,
              value: tag,
            }))}
            onChange={(selections) =>
              field.onChange(selections.map(({ value }) => value))
            }
            error={fieldState.error?.message}
            creatable
          />
        )}
      />
    </>
  );
}
